/**
 * ARCHIVED v1 question bank — frozen, read-only, kept for back-compat with
 * existing v1 submissions. New work goes in question-bank-v2.ts.
 *
 * v1.0.0 question bank — code mirror of docs/02-product/question-bank.md.
 *
 * DO NOT edit values here without updating the doc first
 * (see operating rule: docs are the source of truth). The drift test in
 * `question-bank.test.ts` enforces the doc/code lock.
 */

export const BANK_VERSION_V1 = "v1.0.0" as const;
export const SCORE_VERSION_V1 = "v1.0.0" as const;
export const SCORE_VERSION_INT_V1 = 1;
export const CONTENT_VERSION_V1 = "v1.0.0" as const;

export const DIMENSIONS_V1 = [
  "impulse",
  "security",
  "scarcity",
  "avoidance",
  "identity",
] as const;
export type DimensionV1 = (typeof DIMENSIONS_V1)[number];

export const DIMENSION_TO_ARCHETYPE_V1 = {
  impulse: "spender",
  security: "saver",
  scarcity: "hoarder",
  avoidance: "avoider",
  identity: "shopper",
} as const satisfies Record<DimensionV1, string>;

export type QuestionTypeV1 = "likert" | "scenario" | "frequency";

export interface AnswerOptionV1 {
  /** Raw value as the user picks it. */
  value: number;
  label: string;
}

export interface QuestionV1 {
  id: string;
  dim: DimensionV1;
  type: QuestionTypeV1;
  weight: number;
  text: string;
  answers: ReadonlyArray<AnswerOptionV1>;
}

const LIKERT: ReadonlyArray<AnswerOptionV1> = [
  { value: 1, label: "Strongly disagree" },
  { value: 2, label: "Disagree" },
  { value: 3, label: "Neutral" },
  { value: 4, label: "Agree" },
  { value: 5, label: "Strongly agree" },
];

const FREQUENCY_4: ReadonlyArray<AnswerOptionV1> = [
  { value: 1, label: "Never" },
  { value: 2, label: "Once" },
  { value: 3, label: "A few times" },
  { value: 4, label: "Often" },
];

export const QUESTION_BANK_V1: ReadonlyArray<QuestionV1> = [
  // Impulse (Spender)
  {
    id: "imp_01",
    dim: "impulse",
    type: "likert",
    weight: 1.0,
    text: "I sometimes buy something to feel better about my day.",
    answers: LIKERT,
  },
  {
    id: "imp_02",
    dim: "impulse",
    type: "likert",
    weight: 1.0,
    text: "I've checked out of a cart within minutes of first seeing the product.",
    answers: LIKERT,
  },
  {
    id: "imp_03",
    dim: "impulse",
    type: "likert",
    weight: 1.0,
    text: "My weekend spending often surprises me on Monday.",
    answers: LIKERT,
  },
  {
    id: "imp_04",
    dim: "impulse",
    type: "scenario",
    weight: 1.5,
    text: "An unexpected $200 lands in your account. What's your first instinct?",
    answers: [
      { value: 1, label: "Move it to savings and forget it" },
      { value: 2, label: "Pay down something boring (bill, card)" },
      { value: 3, label: "Wait a week before deciding" },
      { value: 4, label: "Treat yourself to one nice thing" },
      { value: 5, label: "Plan a small trip or experience this month" },
    ],
  },

  // Security (Saver)
  {
    id: "sec_01",
    dim: "security",
    type: "likert",
    weight: 1.0,
    text: "A growing balance gives me more comfort than most experiences.",
    answers: LIKERT,
  },
  {
    id: "sec_02",
    dim: "security",
    type: "likert",
    weight: 1.0,
    text: "I'd rather skip a trip than break my savings rhythm.",
    answers: LIKERT,
  },
  {
    id: "sec_03",
    dim: "security",
    type: "likert",
    weight: 1.0,
    text: "I keep extra cushion beyond what my budget actually needs.",
    answers: LIKERT,
  },
  {
    id: "sec_04",
    dim: "security",
    type: "frequency",
    weight: 1.0,
    text: "In the past month, how often did you decline a paid plan to keep savings intact?",
    answers: FREQUENCY_4,
  },

  // Scarcity (Hoarder)
  {
    id: "sca_01",
    dim: "scarcity",
    type: "likert",
    weight: 1.0,
    text: "Spending money — even on essentials — often feels like losing it.",
    answers: LIKERT,
  },
  {
    id: "sca_02",
    dim: "scarcity",
    type: "likert",
    weight: 1.0,
    text: "I keep items I rarely use because I might need them one day.",
    answers: LIKERT,
  },
  {
    id: "sca_03",
    dim: "scarcity",
    type: "likert",
    weight: 1.0,
    text: "I tend to repair things long past when replacing would cost less.",
    answers: LIKERT,
  },
  {
    id: "sca_04",
    dim: "scarcity",
    type: "likert",
    weight: 1.0,
    text: "I buy in bulk to feel ahead, even when I don't finish what I bought.",
    answers: LIKERT,
  },

  // Avoidance (Avoider)
  {
    id: "avo_01",
    dim: "avoidance",
    type: "likert",
    weight: 1.0,
    text: "I avoid opening my bank app when I expect bad news.",
    answers: LIKERT,
  },
  {
    id: "avo_02",
    dim: "avoidance",
    type: "likert",
    weight: 1.0,
    text: "Bills and statements sit unopened for days at a time.",
    answers: LIKERT,
  },
  {
    id: "avo_03",
    dim: "avoidance",
    type: "likert",
    weight: 1.0,
    text: "I defer money decisions hoping they'll resolve themselves.",
    answers: LIKERT,
  },
  {
    id: "avo_04",
    dim: "avoidance",
    type: "frequency",
    weight: 1.0,
    text: "In the past month, how often did you postpone a money task you'd already planned?",
    answers: FREQUENCY_4,
  },

  // Identity (Shopper)
  {
    id: "idn_01",
    dim: "identity",
    type: "likert",
    weight: 1.0,
    text: "What I buy is part of how I show up in the world.",
    answers: LIKERT,
  },
  {
    id: "idn_02",
    dim: "identity",
    type: "likert",
    weight: 1.0,
    text: "I notice and care about the brand of things I use daily.",
    answers: LIKERT,
  },
  {
    id: "idn_03",
    dim: "identity",
    type: "likert",
    weight: 1.0,
    text: "I'll pay more for the version that feels like 'me.'",
    answers: LIKERT,
  },
  {
    id: "idn_04",
    dim: "identity",
    type: "scenario",
    weight: 1.5,
    text: "You're about to upgrade something everyday (bag, phone, kitchen tool). What pulls you most?",
    answers: [
      { value: 1, label: "Lowest working price" },
      { value: 2, label: "Best reviews regardless of look" },
      { value: 3, label: "The one friends would recognize" },
      { value: 4, label: "Whatever matches my current aesthetic" },
      { value: 5, label: "The signature/premium tier of that category" },
    ],
  },
];

export const TOTAL_QUESTIONS_V1 = QUESTION_BANK_V1.length;

/** O(1) lookup by id. Frozen so callers cannot mutate. */
export const QUESTION_BY_ID_V1: Readonly<Record<string, QuestionV1>> =
  Object.freeze(
    Object.fromEntries(QUESTION_BANK_V1.map((q) => [q.id, q])),
  );

/**
 * Milestone microcopy schedule from docs/03-funnel/funnel-architecture.md.
 * Keyed by the question index (1-based) AFTER which the line should appear.
 */
export const MILESTONES_V1: ReadonlyArray<{ afterIndex: number; line: string }> = [
  { afterIndex: 1, line: "Pick the answer that's closest to true — not the one that sounds best." },
  { afterIndex: 3, line: "Every answer makes the pattern clearer." },
  { afterIndex: 7, line: "You're a few steps away from seeing where your money slips." },
  { afterIndex: 10, line: "Halfway. The interesting questions start now." },
  { afterIndex: 14, line: "This is about spotting the habit, not judging it." },
  { afterIndex: 17, line: "Almost there. Don't overthink the last few." },
];

export function milestoneForIndexV1(index: number): string | null {
  const hit = MILESTONES_V1.find((m) => m.afterIndex === index);
  return hit?.line ?? null;
}
